import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

export interface DownloadProgress {
    current: number;
    total: number;
    percentage: number;
    templateId: string;
}

export class TemplateCache {
    private static CACHE_DIR = 'templates_cache';
    private static cachedTemplates = new Set<string>();
    private static initialized = false;
    private static listeners: ((templateId: string) => void)[] = [];
    private static downloadPromises = new Map<string, Promise<string>>();

    /**
     * Initialize the cache system
     */
    static async initialize(): Promise<void> {
        if (this.initialized) return;

        if (!Capacitor.isNativePlatform()) {
            this.cachedTemplates.clear();
            this.initialized = true;
            return;
        }

        try {
            // Create cache directory if it doesn't exist
            await Filesystem.mkdir({
                path: this.CACHE_DIR,
                directory: Directory.Data,
                recursive: true
            });

            // Load list of cached templates
            await this.loadCachedList();
            this.initialized = true;
        } catch (error) {
            console.error('Failed to initialize template cache:', error);
        }
    }

    /**
     * Subscribe to cache updates
     */
    static addListener(callback: (templateId: string) => void): () => void {
        this.listeners.push(callback);
        return () => this.removeListener(callback);
    }

    static removeListener(callback: (templateId: string) => void): void {
        this.listeners = this.listeners.filter(l => l !== callback);
    }

    private static notifyListeners(templateId: string): void {
        this.listeners.forEach(listener => listener(templateId));
    }

    /**
     * Check if a template is cached locally
     */
    static async isCached(templateId: string): Promise<boolean> {
        await this.initialize();
        return this.cachedTemplates.has(templateId);
    }

    /**
     * Check if a template URL is remote (Firebase hosted)
     */
    static isRemoteUrl(imageUrl: string): boolean {
        return imageUrl.startsWith('http://') || imageUrl.startsWith('https://');
    }

    /**
     * Get the local path for a cached template
     */
    static async getCachedPath(templateId: string): Promise<string | null> {
        if (!Capacitor.isNativePlatform()) {
            return null;
        }

        if (!await this.isCached(templateId)) {
            return null;
        }

        try {
            const result = await Filesystem.getUri({
                path: `${this.CACHE_DIR}/${templateId}`,
                directory: Directory.Data
            });
            return Capacitor.convertFileSrc(result.uri);
        } catch (error) {
            console.error('Failed to get cached path:', error);
            return null;
        }
    }

    /**
     * Download and cache a remote template
     */
    static async downloadTemplate(
        templateId: string,
        imageUrl: string,
        onProgress?: (progress: DownloadProgress) => void
    ): Promise<string> {
        await this.initialize();

        if (!Capacitor.isNativePlatform()) {
            return imageUrl;
        }

        // If already cached, return cached path
        if (await this.isCached(templateId)) {
            const cachedPath = await this.getCachedPath(templateId);
            if (cachedPath) return cachedPath;
        }

        // Check if download is already in progress
        const existingPromise = this.downloadPromises.get(templateId);
        if (existingPromise) {
            return existingPromise;
        }

        // Create new download promise
        const downloadPromise = this._performDownload(templateId, imageUrl, onProgress);
        this.downloadPromises.set(templateId, downloadPromise);

        try {
            const result = await downloadPromise;
            return result;
        } finally {
            // Clean up promise from map
            this.downloadPromises.delete(templateId);
        }
    }

    /**
     * Internal method to perform the actual download
     */
    private static async _performDownload(
        templateId: string,
        imageUrl: string,
        onProgress?: (progress: DownloadProgress) => void
    ): Promise<string> {
        try {
            // Download the image
            if (onProgress) {
                onProgress({ current: 0, total: 100, percentage: 0, templateId });
            }

            const response = await fetch(imageUrl);
            if (!response.ok) {
                throw new Error(`Failed to download: ${response.statusText}`);
            }

            const blob = await response.blob();
            const base64Data = await this.blobToBase64(blob);

            if (onProgress) {
                onProgress({ current: 50, total: 100, percentage: 50, templateId });
            }

            // Save to filesystem
            const fileName = `${templateId}`;
            await Filesystem.writeFile({
                path: `${this.CACHE_DIR}/${fileName}`,
                data: base64Data,
                directory: Directory.Data
            });

            // Add to cached list
            this.cachedTemplates.add(templateId);
            await this.saveCachedList();
            this.notifyListeners(templateId);

            if (onProgress) {
                onProgress({ current: 100, total: 100, percentage: 100, templateId });
            }

            // Return the cached path
            const cachedPath = await this.getCachedPath(templateId);
            return cachedPath || imageUrl;
        } catch (error) {
            console.error('Failed to download template:', error);
            throw error;
        }
    }

    /**
     * Get the image URL for a template (cached if available, remote otherwise)
     */
    static async getImageUrl(templateId: string, remoteUrl: string): Promise<string> {
        // If it's a local URL (starts with /), return as-is
        if (!this.isRemoteUrl(remoteUrl)) {
            return remoteUrl;
        }

        // Check if cached
        if (await this.isCached(templateId)) {
            const cachedPath = await this.getCachedPath(templateId);
            if (cachedPath) return cachedPath;
        }

        // Return remote URL (will need to be downloaded)
        return remoteUrl;
    }

    /**
     * Pre-download multiple templates
     */
    static async downloadMultiple(
        templates: Array<{ id: string; image: string }>,
        onProgress?: (progress: DownloadProgress) => void
    ): Promise<void> {
        const total = templates.length;
        let current = 0;

        for (const template of templates) {
            if (this.isRemoteUrl(template.image)) {
                try {
                    await this.downloadTemplate(template.id, template.image, () => {
                        if (onProgress) {
                            onProgress({
                                current: current,
                                total: total,
                                percentage: Math.round((current / total) * 100),
                                templateId: template.id
                            });
                        }
                    });
                } catch (error) {
                    console.error(`Failed to download template ${template.id}:`, error);
                }
            }
            current++;
        }

        if (onProgress) {
            onProgress({
                current: total,
                total: total,
                percentage: 100,
                templateId: 'complete'
            });
        }
    }

    /**
     * Clear all cached templates
     */
    static async clearCache(): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            this.cachedTemplates.clear();
            return;
        }

        try {
            await Filesystem.rmdir({
                path: this.CACHE_DIR,
                directory: Directory.Data,
                recursive: true
            });

            this.cachedTemplates.clear();
            this.initialized = false;
            await this.initialize();
        } catch (error) {
            console.error('Failed to clear cache:', error);
        }
    }

    /**
     * Get cache size in bytes
     */
    static async getCacheSize(): Promise<number> {
        if (!Capacitor.isNativePlatform()) {
            return 0;
        }

        try {
            const files = await Filesystem.readdir({
                path: this.CACHE_DIR,
                directory: Directory.Data
            });

            let totalSize = 0;
            for (const file of files.files) {
                try {
                    const stat = await Filesystem.stat({
                        path: `${this.CACHE_DIR}/${file.name}`,
                        directory: Directory.Data
                    });
                    totalSize += stat.size;
                } catch {
                    // Ignore errors for individual files
                }
            }

            return totalSize;
        } catch {
            return 0;
        }
    }

    // Private helper methods

    private static async loadCachedList(): Promise<void> {
        try {
            const result = await Filesystem.readFile({
                path: `${this.CACHE_DIR}/cached_list.json`,
                directory: Directory.Data,
                encoding: Encoding.UTF8
            });

            const list = JSON.parse(result.data as string);
            this.cachedTemplates = new Set(list);
        } catch {
            // File doesn't exist yet, that's okay
            this.cachedTemplates = new Set();
        }
    }

    private static async saveCachedList(): Promise<void> {
        try {
            const list = Array.from(this.cachedTemplates);
            await Filesystem.writeFile({
                path: `${this.CACHE_DIR}/cached_list.json`,
                data: JSON.stringify(list),
                directory: Directory.Data,
                encoding: Encoding.UTF8
            });
        } catch (error) {
            console.error('Failed to save cached list:', error);
        }
    }

    private static blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result as string;
                // Remove the data URL prefix
                const base64Data = base64.split(',')[1];
                resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }
}
